'use client';
import * as SP from '@longtail-education/superprep-views-react'
import { createTheme, ThemeProvider } from '@mui/material'
//import log from 'loglevel-es'
import React from 'react'

export interface PopStudyNavBarProps {
  profileName: string|null|undefined,
  photoURL: string|null|undefined,
  isContentManager: boolean,
  activeTab: string,
  onActiveTabChange?: (tab: string) => void,
}

const customTheme = createTheme({
  ...SP.popStudyTheme,
  typography: {
    fontFamily: "'Bryant Pro', 'jaf-domus', sans-serif",
  },
  palette: {
    mode: 'light',
    primary: {
      main: '#381a99',
      light: '#381a99',
      dark: '#381a99',
    },
  }
});

export function PopStudyNavBar({
  profileName,
  photoURL,
  isContentManager,
  activeTab,
  onActiveTabChange,
}: PopStudyNavBarProps) {
  const handleSelect = (item: string) => {
    onActiveTabChange?.(item)
  }
  const mobileTextStyle = {
    color: 'white',
  }
  const textStyle = {
    '& .nav-text': {
      borderBottom: '1px solid transparent', // Add transparent border by default
      paddingBottom: '1px', // Compensate for the border
    },
    '&:hover': {
      '& .nav-text': {
        borderBottomColor: 'inherit' // Only change the color on hover
      }
    }
  }
  const entryStyle = (item: string) => {
    return {
      opacity: item === activeTab ? 1 : 0.5,
      ...textStyle
     }
  }
  //log.debug(customTheme)
  return (
    <ThemeProvider theme={customTheme}>
      <SP.NavBar>
        {/* Mobile menu section */}
        <SP.NavBarMenuSection sx={{ display: { xs: 'block', md: 'none' } }}>
          <SP.SideBar>
            <SP.SideBarHeaderSection>
              <SP.SideBarLogo
                app="PopStudy"
                onClick={() => handleSelect('home')}
              />
            </SP.SideBarHeaderSection>
            <SP.SideBarContentSection>
              <SP.SideBarContentItem
                onClick={() => handleSelect('home')}
                sx={entryStyle('home')}
              >
                <span className="nav-text">Home</span>
              </SP.SideBarContentItem>
              <SP.SideBarContentItem
                onClick={() => handleSelect('library')}
                sx={entryStyle('library')}
              >
                <span className="nav-text">Explore</span>
              </SP.SideBarContentItem>
              <SP.SideBarContentItem
                onClick={() => handleSelect('performance')}
                sx={entryStyle('performance')}
              >
                Performance
              </SP.SideBarContentItem>
              { isContentManager && (
                <SP.SideBarContentItem
                  onClick={() => handleSelect('brushup')}
                  sx={entryStyle('brushup')}
                >
                  <span className="nav-text">Brushup</span>
                </SP.SideBarContentItem>
              )}
              { isContentManager && (
                <SP.SideBarContentItem
                  onClick={() => handleSelect('resources')}
                  sx={entryStyle('resources')}
                >
                  <span className="nav-text">Resources</span>
                </SP.SideBarContentItem>
              )}
              <SP.SideBarDivider />
              <SP.SideBarContentItem
                onClick={() => handleSelect('join')}
              >
                Classroom Join Code
              </SP.SideBarContentItem>
              <SP.SideBarDivider />
              {/* App switcher */}
              <SP.SideBarAppSwitcher>
              <SP.NavBarAppButton
                  app="PopQuiz"
                  onClick={() => handleSelect('popquiz')}
                  sx={mobileTextStyle}
                />
                <SP.NavBarAppButton
                  app="PopTeams"
                  onClick={() => handleSelect('popteams')}
                  sx={mobileTextStyle}
                />
                <SP.NavBarAppButton
                  app="PopStack"
                  onClick={() => handleSelect('popstack')}
                  sx={mobileTextStyle}
                />
              </SP.SideBarAppSwitcher>
              <SP.SideBarDivider />
              <SP.SideBarContentItem
                onClick={() => handleSelect('logout')}
              >
                Log Out
              </SP.SideBarContentItem>
            </SP.SideBarContentSection>
            <SP.SideBarFooterSection>
              <SP.SideBarFeedback onClick={() => handleSelect('feedback')} />
            </SP.SideBarFooterSection>
          </SP.SideBar>
        </SP.NavBarMenuSection>
        {/* Desktop view */}
        <SP.NavBarLeadingSection>
          <SP.NavBarAppSwitcher sx={{ display: { xs: 'none', md: 'block' } }}>
            <SP.NavBarAppButton
              app="PopQuiz"
              onClick={() => handleSelect('popquiz')}
            />
            <SP.NavBarAppButton
              app="PopTeams"
              onClick={() => handleSelect('popteams')}
            />
            <SP.NavBarAppButton
              app="PopStack"
              onClick={() => handleSelect('popstack')}
            />
          </SP.NavBarAppSwitcher>
          <SP.NavBarLogo
            app={'PopStudy'}
            onClick={() => handleSelect('home')}
          />
        </SP.NavBarLeadingSection>

        <SP.NavBarContentSection>
          <SP.NavBarContentItem
            onClick={() => handleSelect('home')}
            sx={entryStyle('home')}
          >
            <span className="nav-text">Home</span>
          </SP.NavBarContentItem>
          <SP.NavBarContentItem
            onClick={() => handleSelect('library')}
            sx={entryStyle('library')}
          >
            <span className="nav-text">Explore</span>
          </SP.NavBarContentItem>
          <SP.NavBarContentItem
            onClick={() => handleSelect('performance')}
            sx={entryStyle('performance')}
          >
            <span className="nav-text">Performance</span>
          </SP.NavBarContentItem>
          { isContentManager && (
            <SP.NavBarContentItem
              onClick={() => handleSelect('brushup')}
              sx={entryStyle('brushup')}
            >
              <span className="nav-text">Brushup</span>
            </SP.NavBarContentItem>
          )}
          { isContentManager && (
            <SP.NavBarContentItem
              onClick={() => handleSelect('resources')}
              sx={entryStyle('resources')}
            >
              <span className="nav-text">Resources</span>
            </SP.NavBarContentItem>
          )}
        </SP.NavBarContentSection>

        <SP.NavBarTrailingSection>
          <SP.NavBarContentItem
            variant='outlined'
            sx={{
              display: { xs: 'none', md: 'block' },
              borderColor: 'white',
              borderRadius: '9999px'
            }}
            onClick={() => handleSelect('join')}
          >
            Classroom Join Code
          </SP.NavBarContentItem>
          <SP.NavBarProfile
            name={profileName ?? ''}
            iconUrl={photoURL ?? undefined}
          >
            <SP.NavBarContentItem
              sx={{ color: 'black' }}
              onClick={() => handleSelect('profile')}
            >
              View Profile
            </SP.NavBarContentItem>
            <SP.NavBarContentItem
              sx={{ color: 'black' }}
              onClick={() => handleSelect('logout')}
            >
              Log out
            </SP.NavBarContentItem>
          </SP.NavBarProfile>
        </SP.NavBarTrailingSection>
      </SP.NavBar>
    </ThemeProvider>
  )
}
